import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Message } from "@chatscope/chat-ui-kit-react";
import { makeRequest } from "../util/AxiosInstance";
import { API_URL } from "../zustand/const";
import { appStore, userStore } from "../zustand/index";
import { handleError } from "../util/functions";
import Order from "./components/OrderModal";
import { Notify } from "notiflix";
import { LoadingButton } from "@mui/lab";

function OrderMessage({ data }) {
  const navigate = useNavigate();
  const { mode: appMode } = appStore();
  const [status, setStatus] = useState("loading");
  const [isLoading, setIsLoading] = useState(false);
  const [isAcceptLoading, setIsAcceptLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const { isLoggedIn } = userStore();
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const id = data?.content;

  const showEditForm = () => {
    setShow(true);
    setEditMode(true);
    document.getElementById("order_modal").showModal();
  };

  const endAction = (prop = null) => {
    setShow(false);
    setEditMode(false);
    getOrderInfo();
  };

  const getOrderInfo = async () => {
    setIsLoading(true);
    makeRequest("GET", `${API_URL}/order/${id}`)
      .then((res) => {
        const dd = res.data?.data;
        setOrder(dd);
        setStatus(dd?.status);
      })
      .catch((err) => {
        const error = handleError(err);
        console.error("Error: ", error);
        setError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const toCheckout = () => {
    navigate(`/checkout?orderId=${id}`);
  };

  const handleOrderStatus = async (action) => {
    let intention;
    let msg;
    switch (action) {
      case "accept":
        intention = "accept";
        msg = "Order accepted";
        break;
      case "reject":
        intention = "reject";
        msg = "Order rejected";
        break;
      case "cancel":
        intention = "cancel";
        msg = "Order canceled";
        break;
      default:
        throw new Error("Invalid action");
    }
    if (intention == "accept") {
      setIsAcceptLoading(true);
    }
    if (intention == "reject") {
      setIsRejectLoading(true);
    }

    makeRequest("PUT", `${API_URL}/order/${id}/${intention}`)
      .then((res) => {
        if (intention == "accept") {
          Notify.success(msg);
          toCheckout();
        }
        if (intention == "reject") {
          Notify.info(msg);
        }
        getOrderInfo();
      })
      .catch((err) => {
        const error = handleError(err);
        Notify.error(error);
      })
      .finally(() => {
        if (intention == "accept") {
          setIsAcceptLoading(false);
        }
        if (intention == "reject") {
          setIsRejectLoading(false);
        }
      });
  };

  const renderAction = () => {
    if (status == "loading") {
      return (
        <>
          <div className="h-8 w-16 bg-gray-300 rounded-md animate-pulse"></div>
          <div className="h-8 w-16 bg-gray-300 rounded-md animate-pulse"></div>
        </>
      );
    }
    if (!isLoggedIn) {
      return (
        <LoadingButton className="btn-sm btn-primary btn-outline">
          Login to Accept
        </LoadingButton>
      );
    }
    if (
      status === 'rejected' ||
      status === 'cancelled' ||
      status === 'expired' ||
      status === 'archived'
    ) {
      return (
        <LoadingButton
          className={"btn-sm btn-error btn-disabled btn-outline"}
          disabled
        >
          {status === 'rejected' && 'Rejected'}
          {status === 'cancelled' && 'Cancelled'}
          {status === 'expired' && 'Expired'}
          {status === 'archived' && 'Archived'}
        </LoadingButton>
      );
    }
    if (status == 'accepted') {
      return (
        <LoadingButton
          onClick={toCheckout}
          className={`btn-sm btn-primary btn-outline ${appMode !== 'customer' && "btn-disabled"} `}
        >
          {status === 'accepted' && appMode === 'customer' ? 'Pay Now' : 'Pending Payment'}
        </LoadingButton>
      );
    }
    if (status == 'ongoing') {
      return (
        <LoadingButton
          className={"btn-sm btn-success btn-disabled btn-outline"}
          disabled
        >
          {status === 'ongoing' && 'Ongoing'}
        </LoadingButton>
      );
    }
    if (status === 'completed') {
      return (
        <LoadingButton
          className={"btn-sm btn-success btn-disabled btn-outline"}
          disabled
        >
          {status === 'completed' && 'Completed'}
        </LoadingButton>
      );
    }
    if (appMode === "admin") {
      return (
        <>
          <LoadingButton onClick={showEditForm} className="btn-sm btn-primary">Edit</LoadingButton>
          <LoadingButton
            onClick={() => handleOrderStatus("cancel")}
            className={"btn-sm btn-error btn-outline"}>
            Cancel
          </LoadingButton>
        </>
      );
    }
    if (appMode === "customer") {
      return (
        <>
          <LoadingButton
            onClick={() => handleOrderStatus("accept")}
            className="btn-sm btn-primary"
            loading={isAcceptLoading}
          >
            Accept
          </LoadingButton>
          <LoadingButton
            onClick={() => handleOrderStatus("reject")}
            className={"btn-sm btn-error btn-outline"}
            loading={isRejectLoading}
          >
            Reject
          </LoadingButton>
        </>
      );
    }
  };

  useEffect(() => {
    getOrderInfo();
  }, []);

  return (
    <>
      <Message
        className="order-message-container w-[69%] sm:w-1/2 sm:min-w-[300px]"
        key={data}
        model={{
          type: "custom",
          sentTime: "just now",
          sender: "any",
          direction: appMode === "customer" ? "incoming" : "outgoing",
        }}
      >
        <Message.CustomContent as="MessageCustomContent" className="">
          <h6 className="text-sm text-gray-500 mb-1 px-4 py-2">
            {appMode !== 'customer'
              ? "You sent an order."
              : "You received an order."}
          </h6>
          <div className="order-message flex flex-col gap-0">
            <div className="order-message-header flex justify-between items-center">
              <h1 className="text-xl font-semibold capitalize">{order?.title}</h1>
              <span className="price">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(order?.price)}
              </span>
            </div>
            <div className="description capitalize">{order?.description}</div>
            <div className="action flex gap-4">{renderAction()}</div>
          </div>
        </Message.CustomContent>
      </Message>
      {show &&
        <Order callback={endAction} context={"chat"} mode={"edit"} id={editMode ? id : undefined} />
      }
    </>
  );
}

export default OrderMessage;
